import type {PremiumStatus} from '../dto/subscription';

/**
 * LA règle de détection du premium côté CLIENTS.
 *
 * Une seule fonction pure décide « premium ou pas » pour le front
 * (`getPremiumStatus` du mainslice) et l'extension (session LinkedIn). Avant,
 * chaque client avait sa propre variante — des règles divergentes, dont
 * certaines prenaient le groupe Cognito pour une source et aucune ne
 * connaissait la licence d'équipe.
 *
 * ⚠️ C'est le MIROIR exact d'`isUserPremium` (`common/src/utils/premium.ts`),
 * la vérité serveur. Ce paquet est front-only : `common` ne peut pas
 * l'importer, donc la règle existe deux fois et les deux doivent rester
 * identiques — toute évolution ici se reporte là-bas, comme
 * `ASSISTANT_LOCALES` avec sa copie Python.
 *
 * Zéro I/O ici : les entrées sont des FAITS déjà collectés, normalement ceux
 * que le serveur renvoie sur `GET /me/premium-status` (licence d'équipe
 * comprise — son lookup DB reste côté serveur).
 *
 * ⚠️ L'attribution manuelle du premium (offrir sans abonnement Stripe) passe
 * désormais par `premiumGrantedAt`, un fait de la BASE. Le groupe Cognito
 * `CUSTOMER` reste une source, en ULTIME recours, le temps que le backfill ait
 * recopié les offres existantes — il est voué à disparaître. Les deux partagent
 * le même rang, après `paused` : ni l'offre ni le groupe ne sont retirés par une
 * mise en pause, donc aucun des deux ne doit primer sur elle (voir la règle).
 */
export interface PremiumFacts {
  stripeSubscriptionId?: string;
  subscriptionPaused?: boolean;
  /** ISO 8601 — porté pour l'affichage, n'entre pas dans le verdict. */
  subscriptionPausedUntil?: string;
  /** ISO 8601 */
  earlyAdoptersPremiumExpireAt?: string;
  hasTeamPremiumLicense?: boolean;
  /**
   * ISO 8601 — premium OFFERT manuellement, porté par la base. Sa présence
   * suffit : la date ne dit pas jusqu'à quand, seulement depuis quand.
   */
  premiumGrantedAt?: string;
}

export type PremiumSource = 'stripe' | 'trial' | 'team' | 'granted';

export interface ResolvedPremiumStatus {
  /** Le `PremiumStatus` historique de `dto/subscription`, réutilisé tel quel. */
  status: PremiumStatus;
  /** `status !== 'free' && status !== 'paused'` — l'accès premium effectif. */
  premium: boolean;
  source?: PremiumSource;
  /**
   * @deprecated Toujours `false`. Le serveur ne renvoie plus l'appartenance au
   * groupe `CUSTOMER`, donc aucune désynchronisation ne peut plus être
   * détectée. Conservé pour que les appelants déjà déployés compilent ; il
   * partira avec le groupe.
   */
  cognitoMismatch: boolean;
}

/**
 * La règle canonique — celle d'`isUserPremium` côté serveur, qui fait foi :
 * 1. Stripe actif ET non pausé → `premium` (source `stripe`) ;
 * 2. sinon `earlyAdoptersPremiumExpireAt` dans le futur → `trial` (premium effectif) ;
 * 3. sinon licence d'équipe → `premium` (source `team`) ;
 * 4. sinon, abonnement pausé → `paused` ;
 * 5. sinon premium OFFERT (`premiumGrantedAt`) → `premium` (source `granted`) ;
 * 6. sinon → `free`.
 *
 * ⚠️ Le groupe Cognito `CUSTOMER` a quitté la règle : il est encore ÉCRIT (des
 * versions publiées de l'extension le lisent dans leur jeton), mais plus aucune
 * lecture ne l'interroge.
 *
 * La pause ne suspend que la source Stripe : un essai en cours ou une licence
 * d'équipe maintient l'accès malgré un abonnement personnel pausé — c'est le
 * comportement historique d'`isUserPremium`, que ce miroir reproduit à
 * l'identique (le front, lui, faisait primer la pause : c'était une des
 * divergences résorbées ici). Seule différence de surface : `isUserPremium`
 * rend un booléen, ici on distingue en plus `trial`/`paused` pour l'UI.
 *
 * ⚠️ L'offre et le groupe sont volontairement les DERNIÈRES sources, après
 * `paused` : elles servent à offrir manuellement le premium à des comptes SANS
 * abonnement Stripe, mais la pause ne les retire pas (rien ne les touche dans
 * le flux de pause) — si elles primaient, mettre en pause ne servirait à rien
 * pour quiconque a le groupe, c'est-à-dire tous les abonnés.
 */
export function resolvePremiumStatus(facts: PremiumFacts, now: Date = new Date()): ResolvedPremiumStatus {
  const hasStripeSubscription = facts.stripeSubscriptionId !== undefined && facts.stripeSubscriptionId !== '';
  const paused = facts.subscriptionPaused === true;

  const trialExpireAt =
    facts.earlyAdoptersPremiumExpireAt !== undefined ? new Date(facts.earlyAdoptersPremiumExpireAt) : undefined;
  // Une date invalide donne NaN, et `NaN > x` est faux : un ISO corrompu retombe sur `free`.
  const trialActive = trialExpireAt !== undefined && trialExpireAt.getTime() > now.getTime();

  let status: PremiumStatus = 'free';
  let source: PremiumSource | undefined;

  if (hasStripeSubscription && !paused) {
    status = 'premium';
    source = 'stripe';
  } else if (trialActive) {
    status = 'trial';
    source = 'trial';
  } else if (facts.hasTeamPremiumLicense === true) {
    status = 'premium';
    source = 'team';
  } else if (hasStripeSubscription && paused) {
    status = 'paused';
  } else if (facts.premiumGrantedAt !== undefined) {
    status = 'premium';
    source = 'granted';
  }

  const premium = status !== 'free' && status !== 'paused';

  return {
    status,
    premium,
    ...(source !== undefined ? {source} : {}),
    // Le serveur ne renvoie plus l'appartenance au groupe : plus rien ne peut
    // signaler une désynchronisation. Conservé le temps que les appelants
    // cessent de le lire, puis retiré avec le groupe lui-même.
    cognitoMismatch: false,
  };
}
